import { useSiteStore } from '~/stores/site.store';

export const useFormatCurrency = (
  value: number | bigint,
  truncated?: boolean,
  customClass?: string,
  useSymbol: boolean = true,
  noCents: boolean = false,
  plainText: boolean = false,
): string => {
  const { getLocale } = useLocaleUtils();
  const siteStore = useSiteStore();
  const notationValue = value > 999 && truncated ? 'compact' : 'standard';
  const copyClass = !!customClass ? customClass : 'gold-gradient-text';
  const formatter = new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: siteStore.getCurrencyCode,
    currencyDisplay: 'narrowSymbol',
    notation: notationValue,
    ...(noCents && { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
  });
  return formatter
    .formatToParts(value)
    .map(({ type, value }) => {
      switch (type) {
        case 'currency':
          return useSymbol
            ? !plainText
              ? `<strong class="${copyClass}">${siteStore.getCurrencySymbol}</strong>`
              : siteStore.getCurrencySymbol
            : '';
        default:
          return value;
      }
    })
    .reduce((string, part) => string + part);
};

export const useFormatNumberCompact = (value: number): string => {
  const { getLocale } = useLocaleUtils();
  const formatter = new Intl.NumberFormat(getLocale(), {
    notation: 'compact',
  });
  return formatter.format(value);
};

export const useFormatToMonthAsString = (value: Date | number): string => {
  const { getLocale } = useLocaleUtils();
  const formatter = new Intl.DateTimeFormat(getLocale(), {
    month: 'long',
  });
  return formatter.format(value);
};

export const useFormatOnlySymbol = (): string => {
  const siteStore = useSiteStore();
  const { getLocale } = useLocaleUtils();
  interface NumberFormatPart {
    type: string;
    value: string;
  }
  const formatter = new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: siteStore.getCurrencyCode,
    currencyDisplay: 'narrowSymbol',
  });
  const parts: NumberFormatPart[] = formatter.formatToParts(0);
  const findCurrency: NumberFormatPart = parts.find(
    (part: NumberFormatPart): boolean => part.type === 'currency',
  );
  return findCurrency.value;
};

export const useFormatTruncateNumber = ({
  value,
  threshold = 999,
  decimalLimit = null,
  removeAllDecimals = false,
}: {
  value: number;
  threshold: number;
  decimalLimit: number | null;
  removeAllDecimals: boolean;
}): string => {
  const notationValue = value > threshold ? 'compact' : 'standard';
  const { getLocale } = useLocaleUtils();
  const formatter = new Intl.NumberFormat(getLocale(), {
    style: 'decimal',
    notation: notationValue,
    roundingMode: 'halfTrunc',
    ...(decimalLimit && { maximumFractionDigits: decimalLimit }),
    ...(removeAllDecimals && { maximumFractionDigits: 0 }),
    ...(notationValue !== 'compact' && { compactDisplay: 'short' }),
  });
  return formatter.format(value);
};

export const useSlugify = (value: string): string => {
  return value
    ?.replace(/ /g, '-')
    ?.trim()
    ?.replace(/\//g, '-')
    ?.replace(/:/g, '')
    ?.replace(/(-+){2}/g, '-')
    ?.replace(/&/g, 'and')
    ?.toLowerCase();
};
export const useReverseSlugify = (value: string): string => {
  return value
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};
